import { defaultItemPriceColor } from "../../components/PriceChip/PriceChip-utls";
import { AssignmentMode } from "./priceFormat-types";
/**
 *
 * @param price - The price to format
 * @param displayTemplate - The string template for displaying the price
 * @param numberFormat - The number format to use for formatting the price
 * @returns
 */
const formatPriceText = (price, displayTemplate, numberFormat) => displayTemplate.replace("{price}", numberFormat.format(Math.abs(price)));
/**
 *
 * @param param0 - The parameters for the price display
 * @param param0.displayPriceOverride - The price override to display. If provided, it is used to return the formatted price without any additional logic or special formatting.
 * @param param0.extraPriceColor - The color for the extra price
 * @param param0.extraPriceDisplay - The display template for the extra price
 * @param param0.mealQuantity - The quantity of meals on the order
 * @param param0.numberFormat - The number format to use
 * @param param0.primaryTextColor - The primary text color
 * @param param0.programMealPrice - The price of the program meal
 * @param param0.recipePrice - The price of the recipe
 * @param param0.assignmentQuantityForMeal - The total quantity of recipes assigned for the specific meal
 * @param param0.reducedPriceColor - The color for the reduced price
 * @param param0.reducedPriceDisplay - The display template for the reduced price
 * @param param0.assignmentPriceText - The text to display for the assignment price when the recipe price is 0 or equal to the program meal price
 * @returns
 */
export const getPriceDisplay = ({ assignmentMode, AssignmentPriceText, displayPriceOverride, ExtraRecipePriceColor, ExtraRecipePriceDisplay = "+ {price}", mealQuantity = 0, numberFormat, programMealPrice, recipePrice, assignmentQuantityForMeal = 0, ReducedRecipePriceColor, ReducedRecipePriceDisplay = "- {price}", shouldUseRecipePricing, }) => {
    // If displayPriceOverride is provided, return it formatted without any additional logic or special formatting.
    if (displayPriceOverride !== undefined) {
        return {
            itemPrice: numberFormat.format(displayPriceOverride),
            itemPriceColor: defaultItemPriceColor,
        };
    }
    if (recipePrice === undefined ||
        programMealPrice === undefined ||
        assignmentQuantityForMeal === undefined ||
        mealQuantity === undefined ||
        assignmentMode === AssignmentMode.NO_CHANGES) {
        return { itemPrice: undefined, itemPriceColor: defaultItemPriceColor };
    }
    const isAddingMeals = assignmentMode === "add_meals" ||
        // assignment mode can only be both on the recipe module
        (assignmentMode === "both" && assignmentQuantityForMeal >= mealQuantity);
    const isAssigningRecipes = assignmentMode === "assign_recipes" ||
        // assignment mode can only be both on the recipe module
        (assignmentMode === "both" && assignmentQuantityForMeal < mealQuantity);
    //* ===== ADD MEALS MODE =====
    if (isAddingMeals) {
        // In add meals mode, we show the recipe price if recipe pricing is enabled.
        // Otherwise, we show the program meal price.
        if (shouldUseRecipePricing) {
            return {
                itemPrice: numberFormat.format(recipePrice),
                itemPriceColor: defaultItemPriceColor,
            };
        }
        return {
            itemPrice: numberFormat.format(programMealPrice),
            itemPriceColor: defaultItemPriceColor,
        };
    }
    //* ===== ASSIGN RECIPES MODE =====
    if (isAssigningRecipes) {
        // In assign recipes mode, we only show the price chip if we are using recipe pricing.
        if (shouldUseRecipePricing && assignmentQuantityForMeal < mealQuantity) {
            const priceDiff = recipePrice - programMealPrice;
            if (recipePrice > 0 && recipePrice < programMealPrice) {
                return {
                    itemPrice: formatPriceText(priceDiff, ReducedRecipePriceDisplay, numberFormat),
                    itemPriceColor: ReducedRecipePriceColor || defaultItemPriceColor,
                };
            }
            if (recipePrice > 0 && recipePrice > programMealPrice) {
                return {
                    itemPrice: formatPriceText(priceDiff, ExtraRecipePriceDisplay, numberFormat),
                    itemPriceColor: ExtraRecipePriceColor || defaultItemPriceColor,
                };
            }
            if (recipePrice === 0 || recipePrice === programMealPrice) {
                return {
                    itemPrice: AssignmentPriceText !== null && AssignmentPriceText !== void 0 ? AssignmentPriceText : undefined,
                    itemPriceColor: defaultItemPriceColor,
                };
            }
        }
        else {
            // If we are in assign recipes mode and not using recipe pricing,
            // we do not show the price chip at all (setting the price to undefined prevents the price chip from being displayed).
            return {
                itemPrice: undefined,
                itemPriceColor: defaultItemPriceColor,
            };
        }
    }
    return { itemPrice: undefined, itemPriceColor: defaultItemPriceColor };
};
//** ===== SUMMARY OF PRICE HANDLING LOGIC =====
// Add Meals Mode
// - If recipe pricing is enabled:
//   - Recipe module displays the full recipe price if recipe price > $0.00.
// - If recipe pricing is not enabled:
//   - Recipe module displays the program meal price.
// Assign Recipes Mode
// - If recipe pricing is enabled and recipe price > $0.00 but < program meal price:
//   - Recipe module displays the price difference (recipe price - program meal price) with the Reduced Recipe Price Display & Color.
// - If recipe pricing is enabled and recipe price > $0.00 and > program meal price:
//   - Recipe module displays the price difference (recipe price - program meal price) with the Extra Recipe Price Display & Color.
// - If recipe pricing is enabled and recipe price = $0.00 or equals program meal price:
//   - Recipe module displays the Assignment Price Text (if configured) or nothing if Assignment Price Text is not configured.
// - If recipe pricing is not enabled:
//   - Recipe module displays nothing (no price chip), regardless of whether Assignment Price Text is configured.
